import React from "react";

import "./requested_role_renderer.less";
import CommentIcon from "@src/assets/icons/comment.svg";
import HandShakeIcon from "@src/assets/icons/handshake.svg";
import AttentionIcon from "@src/assets/icons/warning.svg";
import { COLORS, ROLES } from "@src/constants";
import { useIsOverflowing } from "@src/hooks/use_is_overflowing";
import {
  setDetailsModalVisible,
  setSelectedRequestId,
} from "@src/services/slices/requestOverviewSlice";
import {
  setResourcePlanDetailsModalVisibility,
  setSelectedResourcePlanRequestId,
} from "@src/services/slices/resourcePlanModalSlice";
import { useAppDispatch } from "@src/setupStore";
import {
  AdapterCellRendererProps,
  RequestedRoleRendererValue,
} from "@src/types/custom_renderer_types";
import { Button, Tooltip } from "antd";

interface RequestedRoleRendererProps {
  props: AdapterCellRendererProps<RequestedRoleRendererValue>;
}

const RequestedRoleRenderer: React.FC<RequestedRoleRendererProps> = ({
  props,
}) => {
  const { ref, isOverflowing } = useIsOverflowing<HTMLButtonElement>();

  const dispatch = useAppDispatch();

  const toolTipText =
    "This role has no Team Lead assigned. Please select another relevant role or " +
    "contact the Admin to assign a new Team Lead.";

  const {
    roleRequestId,
    basicRoleName,
    roleSpecificationName,
    actionRequired,
    isUpdated,
    isCommentAvailable,
    latestComment,
    isJointVenture,
    isTeamLeadAssigned,
    isDraft,
  }: RequestedRoleRendererValue = props.adapter(props.data);
  const roleRequestLabel = `${roleRequestId} ${basicRoleName}`;

  function onClick() {
    switch (props.context?.view) {
      case ROLES.TEAM_LEAD:
        handleTeamLeadViewNavigation();
        break;
      case ROLES.PROJECT_LEAD:
        handleProjectLeadViewNavigation();
    }
  }

  function handleProjectLeadViewNavigation() {
    dispatch(setResourcePlanDetailsModalVisibility(true));
    dispatch(setSelectedResourcePlanRequestId(roleRequestId));
  }

  function handleTeamLeadViewNavigation() {
    dispatch(setDetailsModalVisible(true));
    dispatch(setSelectedRequestId(roleRequestId));
  }

  return (
    <div className="requested-role-renderer">
      <div className="requested-role-renderer__standard-role-wrapper">
        <div className="requested-role-renderer__updated-section">
          {isUpdated && isTeamLeadAssigned && (
            <div className="requested-role-renderer__updated-dot" role="circle">
              <span />
            </div>
          )}

          {!isTeamLeadAssigned && (
            <div className="requested-role-renderer__attention-icon">
              <Tooltip placement="top" title={toolTipText}>
                <AttentionIcon role="attention" />
              </Tooltip>
            </div>
          )}
        </div>

        <div className="requested-role-renderer__standard-role">
          <Tooltip
            placement="top"
            title={isOverflowing ? roleRequestLabel : undefined}
          >
            <Button
              ref={ref}
              type="link"
              className={
                "requested-role-renderer__button " +
                (actionRequired ? "requested-role-renderer__button--bold" : "")
              }
              data-testid={`requested-role-${roleRequestId}`}
              onClick={onClick}
            >
              {roleRequestLabel}
            </Button>
          </Tooltip>

          <div className="requested-role-renderer__role-specification">
            {roleSpecificationName}
          </div>
        </div>
      </div>

      <div className="requested-role-renderer__icon-section">
        {isJointVenture && isDraft && (
          <HandShakeIcon
            data-testid="joint-venture-icon"
            fill={COLORS.BRAND_100}
          />
        )}
        {isCommentAvailable && (
          <Tooltip mouseEnterDelay={0.5} placement="top" title={latestComment}>
            <CommentIcon data-testid="comment-icon" />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default RequestedRoleRenderer;
